import toast from 'react-hot-toast';

/** API base URL for all fetch calls (used by slice thunks). Change only here. */
const PROD_API_URL = 'https://smartpmapi.netsense.com.tr';
const LOCAL_API_URL = 'http://localhost:5000';
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
export const API_BASE_URL = isLocalhost ? LOCAL_API_URL : PROD_API_URL;

const TOKEN_KEY = 'smartpm_token';

/** Parse API error response (JSON detail/message or plain text) and show toast */
function handleApiError(res: Response, text: string): string {
  let message = text || `HTTP ${res.status}`;
  try {
    const json = JSON.parse(text);
    if (typeof json.detail === 'string') message = json.detail;
    else if (typeof json.message === 'string') message = json.message;
    else if (typeof json.title === 'string' && typeof json.detail === 'string') message = `${json.title}: ${json.detail}`;
  } catch {
    // keep message as text
  }
  toast.error(message);
  return message;
}

/** Headers with auth token for authenticated API requests */
export function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
}

/** Fetch helper for GET request with auth */
export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    const message = handleApiError(res, text);
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Fetch helper for POST with body */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    const message = handleApiError(res, text);
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Fetch helper for PATCH with body */
export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    const message = handleApiError(res, text);
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Fetch helper for DELETE. Optional body for endpoints that require it (e.g. Vault delete with VaultDeleteDto). */
export async function apiDelete(path: string, body?: unknown): Promise<void> {
  const init: RequestInit = {
    method: 'DELETE',
    headers: getAuthHeaders(),
  };
  if (body !== undefined && body !== null) {
    (init as RequestInit & { body: string }).body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE_URL}${path}`, init);
  if (!res.ok) {
    const text = await res.text();
    const message = handleApiError(res, text);
    throw new Error(message);
  }
}
